/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, HostListener, Inject, NgZone, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CanLeaveDialog, isViewDirty } from '@gh/common-shared';
import { DialogInput } from '@gh/core-ui';
import { logError } from '@gh/core-util';
import { filter$, first$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { RootLayout, SidebarOptions } from '../../../components';
import { ToolbarOptions } from '../../../components/root-layout/toolbar-options';
import { ContextRouteAttachment } from '../router/context-router.interfaces';
import { ContextRouterOutlet } from '../router/context-router.service';
import { CONTEXTABLE_COMPONENT, ContextableComponent } from './contextable-component';

/**
 * This components allows to render context routes into dialogs.
 * It renders dialog on full page size, forbids default closing functionality (disableClose) and has the root toolbar.
 */
@Component({
  selector: 'gh-contextable-dialog',
  styles: [require('./contextable-dialog.component.scss')],
  template: require('./contextable-dialog.component.html'),
  providers: [
    { provide: CONTEXTABLE_COMPONENT, useExisting: ContextableDialogComponent },
    { provide: RootLayout, useExisting: ContextableDialogComponent },
  ],
})
export class ContextableDialogComponent<I> implements OnInit,
                                                      OnDestroy,
                                                      CanLeaveDialog,
                                                      ContextableComponent,
                                                      RootLayout,
                                                      DialogInput<I> {
  input: I;

  @ViewChild('container', { read: ViewContainerRef, static: true }) container: ViewContainerRef;

  readonly attachment = ContextRouteAttachment.Dialog;

  options?: ToolbarOptions;

  private outlet: ContextRouterOutlet;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private ngZone: NgZone,
              private dialogRef: MatDialogRef<any>) {

  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeyPress(): void {
    this.cancel();
  }

  ngOnInit(): void {
    const { outlet } = this.dialogData['context'];

    outlet.attach(this.container);

    this.outlet = outlet;
  }

  ngOnDestroy(): void {
    this.outlet.destroy();
  }

  isViewDirty(): boolean {
    return isViewDirty(this.outlet.componentRef.instance);
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    const component = this.outlet.componentRef.instance;
    if (component.canDeactivate) {
      return component.canDeactivate();
    } else {
      return true;
    }
  }

  /**
   * On success close this dialog with provided value
   */
  apply(value: any): void {
    this.dialogRef.close(value);
  }

  /**
   * On cancel just close dialog, but we respect canDeactivate flow.
   */
  cancel(): void {
    this.outlet.canDeactivate().pipe(
      filter$(Boolean))
      .subscribe(() => this.dialogRef.close(), logError);
  }

  setToolbarOptions(options: ToolbarOptions): void {
    this.ngZone.onMicrotaskEmpty.pipe(first$()).subscribe(() => {
      this.options = options;
    });
  }

  resetToolbarOptions(): void {
    this.ngZone.onMicrotaskEmpty.pipe(first$()).subscribe(() => {
      this.options = void 0;
    });
  }

  hideSidebar(): void {
    throw new Error('ContextableDialogComponent: sidebar is note supported');
  }

  showSidebar(options: SidebarOptions): void {
    throw new Error('ContextableDialogComponent: sidebar is note supported');
  }
}
